import React from "react";
import { motion } from "framer-motion";
import { CalendarCheck, ArrowRight, Play, Sparkles, BookOpen } from "lucide-react";
import FloatingLetters from "./FloatingLetters";
import Logo from "./Logo";

const CALENDLY = "https://calendly.com/wordgumbolearningco";

export default function Hero() {
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative pt-32 md:pt-40 pb-20 md:pb-28 overflow-hidden"
    >
      {/* Soft cream background + decorative dots */}
      <div className="absolute inset-0 wg-dots opacity-60" aria-hidden="true" />
      <div
        aria-hidden="true"
        className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-wg-yellow/20 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="absolute -bottom-32 -left-32 w-[520px] h-[520px] rounded-full bg-wg-teal/15 blur-3xl"
      />

      <FloatingLetters />

      <div className="relative max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
        {/* Left: Copy */}
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 bg-white rounded-full px-4 py-2 border border-wg-purple/15 shadow-sm"
          >
            <Sparkles className="w-4 h-4 text-wg-yellow" />
            <span className="text-xs font-semibold uppercase tracking-[0.16em] text-wg-purple">
              Belle Chasse, Louisiana
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="mt-6 font-heading font-extrabold text-5xl md:text-6xl xl:text-7xl tracking-tight leading-[1.04] text-wg-ink"
          >
            Helping children{" "}
            <span className="text-wg-orange">Read</span>,{" "}
            <span className="text-wg-teal">Learn</span>,{" "}
            <span className="text-wg-green">Grow</span>, and{" "}
            <span className="text-wg-purple">Succeed</span>.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-6 text-lg md:text-xl leading-relaxed text-wg-ink2 max-w-2xl"
          >
            Personalized dyslexia therapy, reading tutoring, dyslexia screeners, and full
            dyslexia core assessments for children and families in Belle Chasse, Louisiana.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-8 flex flex-wrap gap-3"
          >
            <a
              href={CALENDLY}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-schedule-consultation-btn"
              className="inline-flex items-center gap-2 bg-wg-purple text-white rounded-full px-7 py-4 font-semibold hover:bg-wg-purpleDeep hover:-translate-y-0.5 shadow-lg shadow-wg-purple/30 transition-all"
            >
              <CalendarCheck className="w-5 h-5" />
              Schedule a Free Consultation
            </a>
            <a
              href="#services"
              data-testid="hero-explore-services-btn"
              className="inline-flex items-center gap-2 bg-white border-2 border-wg-purple/15 text-wg-purple rounded-full px-7 py-4 font-semibold hover:border-wg-purple hover:-translate-y-0.5 transition-all"
            >
              Explore Services
              <ArrowRight className="w-5 h-5" />
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="mt-8 flex items-center gap-3 text-sm md:text-[15px] text-wg-ink2"
          >
            <BookOpen className="w-5 h-5 text-wg-teal" />
            <p className="max-w-xl">
              Led by a <strong className="text-wg-ink">certified Reading Specialist & Dyslexia Therapist</strong>{" "}
              with over 20 years of education experience.
            </p>
          </motion.div>
        </div>

        {/* Right: Hero visual / video placeholder */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="lg:col-span-5 relative"
        >
          <div className="relative rounded-[2.2rem] bg-gradient-to-br from-wg-purple to-wg-purpleDeep p-3 shadow-[0_30px_80px_-20px_rgba(74,29,82,0.45)]">
            <div className="relative rounded-[1.8rem] overflow-hidden bg-wg-cream2 aspect-[4/5]">
              {/* Video placeholder (user will attach video) */}
              <video
                data-testid="hero-video"
                className="absolute inset-0 w-full h-full object-cover"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                poster="https://images.pexels.com/photos/8612922/pexels-photo-8612922.jpeg?auto=compress&cs=tinysrgb&w=900"
                aria-label="Children learning to read"
              >
                <source
                  src="https://customer-assets.emergentagent.com/job_word-gumbo-learn/artifacts/m1hx0520_5892370-uhd_3840_2160_30fps.mp4"
                  type="video/mp4"
                />
              </video>

              {/* Overlay play badge */}
              <div className="absolute bottom-5 left-5 inline-flex items-center gap-2 bg-white/90 backdrop-blur rounded-full pl-2 pr-4 py-2 shadow-lg">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-wg-orange text-white">
                  <Play className="w-4 h-4 ml-0.5" />
                </span>
                <span className="text-xs font-semibold text-wg-purple">Meet Word Gumbo</span>
              </div>
            </div>
          </div>

          {/* Floating credentials chip */}
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6 }}
            className="absolute -left-4 md:-left-10 bottom-8 bg-white rounded-2xl px-5 py-4 shadow-xl border border-wg-purple/10 max-w-[230px]"
          >
            <div className="flex items-center gap-3">
              <Logo className="h-12 w-auto" />
              <div>
                <div className="text-[11px] uppercase tracking-wider text-wg-inkMuted font-semibold">Founder</div>
                <div className="font-heading font-bold text-wg-ink">Laura Cummings</div>
              </div>
            </div>
            <div className="mt-2 text-[12px] text-wg-ink2">Reading Specialist · Dyslexia Therapist</div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
